import type { Draft } from './commands.ts';

export const HISTORY_LIMIT = 200;
/** Edits of the same field closer together than this are one undo step (typing a name, a number). */
export const COALESCE_MS = 1_000;

/** What the last change was, so quick edits of the same field merge into one undo step. */
export interface Coalesce {
  key: string;
  at: number;
}

/** Undo / redo of the editor (BB-114): the last 200 drafts back, cleared forward by a new change. */
export interface History {
  past: Draft[];
  present: Draft;
  future: Draft[];
  last?: Coalesce;
}

export const startHistory = (present: Draft): History => ({ past: [], present, future: [] });

/**
 * A new present. With `coalesce`, a change of the same key within `COALESCE_MS` of the previous one replaces the
 * present instead of adding an undo step (the step stays where the first of those edits began).
 */
export function record(h: History, next: Draft, coalesce?: Coalesce): History {
  if (next === h.present) return h;
  if (coalesce && h.last && h.past.length > 0 && h.last.key === coalesce.key && coalesce.at - h.last.at < COALESCE_MS) {
    return { past: h.past, present: next, future: [], last: coalesce };
  }
  return { past: [...h.past, h.present].slice(-HISTORY_LIMIT), present: next, future: [], ...(coalesce ? { last: coalesce } : {}) };
}

export function undo(h: History): History {
  const previous = h.past.at(-1);
  return previous ? { past: h.past.slice(0, -1), present: previous, future: [h.present, ...h.future] } : h;
}

export function redo(h: History): History {
  const [next, ...rest] = h.future;
  return next ? { past: [...h.past, h.present], present: next, future: rest } : h;
}
